/**
 * Error policy for watching a Nova run (pure, no React / network imports so it
 * is unit-testable). A run that no longer exists, or belongs to another
 * account, is a terminal state: polling it again can never succeed. Anything
 * else is transient and retried with backoff, up to a hard stop when nothing
 * has ever loaded, so the panel can never spin forever.
 */

/** Error codes that mean "this run is gone for this viewer": stop polling. */
export const TERMINAL_RUN_WATCH_ERROR_CODES: ReadonlySet<string> = new Set([
  'NOVA_NOT_FOUND',
  'HTTP_404',
  'HTTP_403',
]);

/** Backoff after the 1st, 2nd, 3rd, 4th and every later consecutive failure. */
export const RUN_WATCH_BACKOFF_MS = [3000, 6000, 12000, 24000, 30000] as const;

/** Consecutive failures, with nothing ever loaded, before the watch gives up. */
export const RUN_WATCH_MAX_FAILURES = 5;

export type RunWatchErrorKind = 'terminal' | 'transient';

function errorCodeOf(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** NOVA_NOT_FOUND / HTTP_404 / HTTP_403 are terminal; everything else is transient. */
export function classifyRunWatchError(error: unknown): RunWatchErrorKind {
  const code = errorCodeOf(error);
  return code && TERMINAL_RUN_WATCH_ERROR_CODES.has(code)
    ? 'terminal'
    : 'transient';
}

/** Delay before retrying after the Nth consecutive failure (1-based). */
export function runWatchRetryDelayMs(consecutiveFailures: number): number {
  const index = Math.min(
    Math.max(consecutiveFailures, 1) - 1,
    RUN_WATCH_BACKOFF_MS.length - 1,
  );
  return RUN_WATCH_BACKOFF_MS[index];
}

export interface RunWatchFailurePlan {
  /** The run is gone (deleted, or another account's): stop, never retry. */
  notFound: boolean;
  /** Transient failures used up without any successful fetch: stop. */
  exhausted: boolean;
  /** When to fetch again, or null to stop polling. */
  retryInMs: number | null;
}

/**
 * What to do after a failed fetch. `consecutiveFailures` includes this one;
 * `hasLoaded` is true once any fetch for this run has succeeded (a run that
 * loaded once keeps retrying at the capped backoff instead of giving up).
 */
export function planRunWatchFailure(params: {
  error: unknown;
  consecutiveFailures: number;
  hasLoaded: boolean;
}): RunWatchFailurePlan {
  if (classifyRunWatchError(params.error) === 'terminal') {
    return { notFound: true, exhausted: false, retryInMs: null };
  }
  if (!params.hasLoaded && params.consecutiveFailures >= RUN_WATCH_MAX_FAILURES) {
    return { notFound: false, exhausted: true, retryInMs: null };
  }
  return {
    notFound: false,
    exhausted: false,
    retryInMs: runWatchRetryDelayMs(params.consecutiveFailures),
  };
}

/**
 * Internal reason codes a run can carry in `errorSummary` (the stale-run
 * reaper's `timed_out`) are never shown raw: they read as the standard failed
 * copy instead.
 */
export const INTERNAL_RUN_FAILURE_CODES: ReadonlySet<string> = new Set([
  'timed_out',
]);

/**
 * Why a research task run failed, as stored in `errorSummary` by the
 * executor. Each reads as `nova.runs.failReason.<code>`, never the raw code.
 */
export const NOVA_RUN_FAILURE_REASON_CODES = [
  'research_no_sources',
  'research_failed',
  'research_limit',
  'sources_unreadable',
  'capability_unavailable',
  'credits_exhausted',
  'report_failed',
  'render_failed',
  'invalid_plan',
] as const;

/** The i18n key for a known failure code, or null for anything else. */
export function runFailureReasonKey(
  errorSummary?: string | null,
): string | null {
  const code = errorSummary?.trim() ?? '';
  return (NOVA_RUN_FAILURE_REASON_CODES as readonly string[]).includes(code)
    ? `nova.runs.failReason.${code}`
    : null;
}

/** The errorSummary to show for a run, or null when the standard copy applies. */
export function visibleRunErrorSummary(
  errorSummary?: string | null,
): string | null {
  if (
    !errorSummary ||
    INTERNAL_RUN_FAILURE_CODES.has(errorSummary) ||
    runFailureReasonKey(errorSummary)
  ) {
    return null;
  }
  return errorSummary;
}

/** Reasons a scheduled fire was refused, as stored on the skipped run row. */
export const NOVA_SKIP_REASON_CODES = [
  'entitlement',
  'inactive',
  'no_version',
  'rate_automation',
  'rate_user',
  'rate_concurrency',
  'kill_switch',
] as const;

export type NovaSkipReasonCode = (typeof NOVA_SKIP_REASON_CODES)[number];

// Older rows spell a few reasons differently: a pre-created engine run marked
// skipped at a cap stores the bare scope, and the operator switch has its own
// flag name.
const SKIP_REASON_ALIASES: Readonly<Record<string, NovaSkipReasonCode>> = {
  automation: 'rate_automation',
  user: 'rate_user',
  concurrency: 'rate_concurrency',
  global_kill_switch: 'kill_switch',
};

/** Generic skipped copy for a reason this client does not know. */
export const GENERIC_SKIP_REASON_KEY = 'nova.runs.runStatus.skipped';

/**
 * The i18n key for a skipped run's reason (`nova.runs.skipReason.<code>`),
 * never the raw code. Unknown or missing codes fall back to the generic
 * skipped copy.
 */
export function skipReasonKey(errorSummary?: string | null): string {
  const raw = errorSummary?.trim() ?? '';
  const code =
    SKIP_REASON_ALIASES[raw] ??
    (NOVA_SKIP_REASON_CODES as readonly string[]).find((known) => known === raw);
  return code ? `nova.runs.skipReason.${code}` : GENERIC_SKIP_REASON_KEY;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The line shown under a run in history: a skipped run's readable reason, a
 * failure's summary (internal codes read as the standard failed copy), or
 * null when the run carries no summary.
 */
export function runErrorSummaryText(
  t: Translate,
  run: { status: string; errorSummary?: string | null },
): string | null {
  if (!run.errorSummary) return null;
  if (run.status === 'skipped') return t(skipReasonKey(run.errorSummary));
  return runFailureHint(t, run.errorSummary);
}

/**
 * The hint under a failed run: a research failure's readable reason, the
 * run's own summary, or the standard failed copy. Never an internal code.
 */
export function runFailureHint(
  t: Translate,
  errorSummary?: string | null,
): string {
  const reasonKey = runFailureReasonKey(errorSummary);
  if (reasonKey) return t(reasonKey);
  return visibleRunErrorSummary(errorSummary) ?? t('nova.runPanel.failedHint');
}
